import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MeetingTranscriptSegmentDto } from '@interfaces/meetings/responses/meeting.response';
import { SharedModule } from '@src/app/shared/shared.module';
import { formatarMarcaDeTempo } from '@features/dashboard/meetings/components/meeting-presenter';
import {
  ParteDoTexto,
  falasComOTermo,
  indiceDaFalaAtual,
  partesDestacadas,
} from '@features/dashboard/meetings/pages/meeting-detail/transcript-sync';

/** Uma fala pronta para o template. */
interface FalaNaTela {
  indice: number;
  quem: string;
  tempo: string;
  segundos: number | null;
  partes: ParteDoTexto[];
}

/**
 * A transcrição da tela de detalhe (card 3162; desde o redesenho, na aba Transcrição ao lado do vídeo):
 * uma fala por bloco, com quem fala e o tempo.
 *
 * - **Clicar no tempo** leva o vídeo para aquele ponto (`irPara`).
 * - **A fala que está tocando** fica destacada e a lista rola até ela, acompanhando o vídeo. Enquanto
 *   há busca, ou com **Seguir o vídeo** desligado, a lista não rola sozinha: quem busca está lendo
 *   outro trecho.
 * - **Buscar na transcrição** destaca as ocorrências (sem acento e sem caixa), com "anterior" e
 *   "próxima" para andar entre elas.
 */
@Component({
  selector: 'app-meeting-transcript-panel',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './meeting-transcript-panel.component.html',
  styleUrl: './meeting-transcript-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingTranscriptPanelComponent {
  readonly falas = input<MeetingTranscriptSegmentDto[]>([]);
  /** Segundo atual do vídeo. */
  readonly tempoAtual = input(0);
  /** Há vídeo: o tempo vira botão. */
  readonly temVideo = input(false);

  readonly irPara = output<number>();

  protected readonly termo = signal('');
  /** A lista acompanha o vídeo (desligável, para ler outro trecho com o vídeo tocando). */
  protected readonly seguirVideo = signal(true);
  /** Qual das ocorrências está selecionada (índice em `ocorrencias`). */
  protected readonly ocorrenciaAtual = signal(0);

  private readonly lista = viewChild<ElementRef<HTMLElement>>('lista');

  protected readonly ocorrencias = computed(() => falasComOTermo(this.falas(), this.termo()));

  protected readonly falaAtual = computed(() =>
    this.temVideo() ? indiceDaFalaAtual(this.falas(), this.tempoAtual()) : -1
  );

  protected readonly falasNaTela = computed<FalaNaTela[]>(() => {
    const termo = this.termo();
    return this.falas().map((fala, indice) => ({
      indice,
      quem: fala.speaker?.trim() || 'Participante',
      tempo: formatarMarcaDeTempo(fala.startSeconds),
      segundos: fala.startSeconds ?? null,
      partes: partesDestacadas(fala.text, termo),
    }));
  });

  constructor() {
    // Acompanha o vídeo: rola até a fala que está tocando, sem mexer na rolagem da página.
    effect(() => {
      const indice = this.falaAtual();
      if (indice < 0 || !this.seguirVideo() || this.termo().trim()) return;
      this.rolarAte(indice);
    });
  }

  protected buscar(termo: string): void {
    this.termo.set(termo ?? '');
    this.ocorrenciaAtual.set(0);
    const primeira = this.ocorrencias()[0];
    if (primeira !== undefined) this.rolarAte(primeira);
  }

  protected andar(passo: 1 | -1): void {
    const total = this.ocorrencias().length;
    if (!total) return;
    const proxima = (this.ocorrenciaAtual() + passo + total) % total;
    this.ocorrenciaAtual.set(proxima);
    this.rolarAte(this.ocorrencias()[proxima]);
  }

  protected ehOcorrenciaAtual(indice: number): boolean {
    return this.ocorrencias()[this.ocorrenciaAtual()] === indice;
  }

  protected ir(fala: FalaNaTela): void {
    if (fala.segundos !== null) this.irPara.emit(fala.segundos);
  }

  /** Rola só a lista (não a página) até a fala, deixando-a no terço de cima. */
  private rolarAte(indice: number): void {
    const lista = this.lista()?.nativeElement;
    const item = lista?.querySelector<HTMLElement>(`[data-fala="${indice}"]`);
    if (!lista || !item) return;
    lista.scrollTo({
      top: Math.max(0, item.offsetTop - lista.clientHeight / 3),
      behavior: 'smooth',
    });
  }
}
